// Local durable mirror of consultation_sessions / consultation_documents,
// used so the Care Consultation wizard can be filled out (including signed
// PDFs) with zero connectivity. consultationSync.js drains whatever's marked
// `synced: false` here once the device is back online.

import { openDB } from 'idb';

const DB_NAME = 'hi_consultation_offline';
const DB_VERSION = 1;
const SESSIONS_STORE = 'pending_sessions';
const DOCUMENTS_STORE = 'pending_documents';

const docKey = (sessionId, templateKey) => `${sessionId}::${templateKey}`;

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
      db.createObjectStore(SESSIONS_STORE, { keyPath: 'id' });
    }
    if (!db.objectStoreNames.contains(DOCUMENTS_STORE)) {
      db.createObjectStore(DOCUMENTS_STORE, { keyPath: 'localKey' });
    }
  },
});

// --- sessions ---

export async function saveSessionLocal(session) {
  const db = await dbPromise;
  const existing = await db.get(SESSIONS_STORE, session.id);
  await db.put(SESSIONS_STORE, { ...existing, ...session, synced: false, updatedAt: Date.now() });
}

export async function getSessionLocal(id) {
  const db = await dbPromise;
  return db.get(SESSIONS_STORE, id);
}

export async function getAllLocalSessions() {
  const db = await dbPromise;
  return db.getAll(SESSIONS_STORE);
}

export async function markSessionSynced(id) {
  const db = await dbPromise;
  const row = await db.get(SESSIONS_STORE, id);
  if (row) await db.put(SESSIONS_STORE, { ...row, synced: true });
}

export async function getUnsyncedSessions() {
  const all = await getAllLocalSessions();
  return all.filter(s => !s.synced);
}

export async function deleteSessionLocal(id) {
  const db = await dbPromise;
  await db.delete(SESSIONS_STORE, id);
  const docs = await getDocumentsForSession(id);
  const tx = db.transaction(DOCUMENTS_STORE, 'readwrite');
  await Promise.all(docs.map(d => tx.store.delete(d.localKey)));
  await tx.done;
}

// --- documents ---

export async function saveDocumentLocal(sessionId, templateKey, data) {
  const db = await dbPromise;
  const key = docKey(sessionId, templateKey);
  const existing = await db.get(DOCUMENTS_STORE, key);
  await db.put(DOCUMENTS_STORE, {
    ...existing,
    localKey: key,
    sessionId,
    templateKey,
    ...data,
    synced: false,
    updatedAt: Date.now(),
  });
}

export async function getDocumentsForSession(sessionId) {
  const db = await dbPromise;
  const all = await db.getAll(DOCUMENTS_STORE);
  return all.filter(d => d.sessionId === sessionId);
}

export async function markDocumentSynced(sessionId, templateKey) {
  const db = await dbPromise;
  const key = docKey(sessionId, templateKey);
  const row = await db.get(DOCUMENTS_STORE, key);
  // Blob only gets cleared once it's confirmed durably stored server-side —
  // no reason to keep megabytes of already-uploaded PDFs sitting in IndexedDB.
  if (row) await db.put(DOCUMENTS_STORE, { ...row, synced: true, pdfBlob: null });
}

export async function getUnsyncedDocuments() {
  const db = await dbPromise;
  const all = await db.getAll(DOCUMENTS_STORE);
  return all.filter(d => !d.synced);
}
